import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import NewInvoice from "@/pages/NewInvoice";
import PrepareInvoice from "@/pages/PrepareInvoice";
import TaxSetupInvoice from "@/pages/TaxSetupInvoice";
import ThreeWayException from "@/pages/ThreeWayException";
import ReturnsToSupplier from "@/pages/ReturnsToSupplier";
import DebitNote from "@/pages/DebitNote";
import CreditNote from "@/pages/CreditNote";
import SupplierLedger from "@/pages/SupplierLedger";
import RoundOffAdjustment from "@/pages/RoundOffAdjustment";
import SupplierPOs from "@/pages/SupplierPOs";
import FreightInvoices from "@/pages/FreightInvoices";
import Allocations from "@/pages/Allocations";
import CustomsDuty from "@/pages/CustomsDuty";
import Settings from "@/pages/Settings";
import AuditLog from "@/pages/AuditLog";
import AccessManagement from "@/pages/AccessManagement";
import SapSyncControl from "@/pages/SapSyncControl";
import GLMappings from "@/pages/GLMappings";
import PendingAccess from "@/pages/PendingAccess";
import AccessDenied from "@/pages/AccessDenied";
import SupplierChargesReport from "@/pages/SupplierChargesReport";
import { hasPageAccess, isPendingAccess } from "@/utils/access";

function ProtectedRoute({ children, page }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A6755] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#6EDD5B] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-white/60 font-mono">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (isPendingAccess(user)) return <PendingAccess />;
  if (page && !hasPageAccess(user, page)) return <Layout><AccessDenied /></Layout>;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute page="dashboard"><Dashboard /></ProtectedRoute>} />
      <Route path="/invoices/new" element={<ProtectedRoute page="invoice_new"><NewInvoice /></ProtectedRoute>} />
      <Route path="/invoices/prepare/:worklistId" element={<ProtectedRoute page="invoice_new"><PrepareInvoice /></ProtectedRoute>} />
      <Route path="/invoices/tax-setup/:worklistId" element={<ProtectedRoute page="invoice_new"><TaxSetupInvoice /></ProtectedRoute>} />
      <Route path="/invoices/exceptions/:exceptionId" element={<ProtectedRoute page="invoice_new"><ThreeWayException /></ProtectedRoute>} />
      <Route path="/invoices" element={<Navigate to="/invoices/new" replace />} />
      <Route path="/returns" element={<ProtectedRoute page="invoice_history"><ReturnsToSupplier /></ProtectedRoute>} />
      <Route path="/invoices/debit-note" element={<ProtectedRoute page="debit_note"><DebitNote /></ProtectedRoute>} />
      <Route path="/invoices/credit-note" element={<ProtectedRoute page="credit_note"><CreditNote /></ProtectedRoute>} />
      <Route path="/supplier-ledger" element={<ProtectedRoute page="supplier_ledger"><SupplierLedger /></ProtectedRoute>} />
      <Route path="/invoices/adjustment" element={<ProtectedRoute page="invoice_new"><RoundOffAdjustment /></ProtectedRoute>} />
      <Route path="/supplier-pos" element={<ProtectedRoute page="supplier_pos"><SupplierPOs /></ProtectedRoute>} />
      <Route path="/supplementary-invoices/new" element={<ProtectedRoute page="landed_cost_invoices"><FreightInvoices /></ProtectedRoute>} />
      <Route path="/supplementary-invoices" element={<ProtectedRoute page="landed_cost_invoices"><FreightInvoices /></ProtectedRoute>} />
      <Route path="/supplementary-invoices/customs-duty" element={<ProtectedRoute page="customs_duty"><CustomsDuty /></ProtectedRoute>} />
      <Route path="/cost-allocations" element={<ProtectedRoute page="allocations"><Allocations /></ProtectedRoute>} />
      <Route path="/landed-cost/invoices" element={<Navigate to="/supplementary-invoices" replace />} />
      <Route path="/landed-cost/allocations" element={<Navigate to="/cost-allocations" replace />} />
      <Route path="/landed-cost" element={<Navigate to="/supplementary-invoices" replace />} />
      <Route path="/customs-duty" element={<Navigate to="/supplementary-invoices/customs-duty" replace />} />
      <Route path="/audit-log" element={<ProtectedRoute page="audit_log"><AuditLog /></ProtectedRoute>} />
      <Route path="/reports/supplier-charges" element={<ProtectedRoute page="supplier_charges_report"><SupplierChargesReport /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute page="settings"><Settings /></ProtectedRoute>} />
      <Route path="/access-management" element={<ProtectedRoute page="access_management"><AccessManagement /></ProtectedRoute>} />
      <Route path="/sap-syncs" element={<ProtectedRoute page="sap_syncs"><SapSyncControl /></ProtectedRoute>} />
      <Route path="/gl-mappings" element={<ProtectedRoute page="gl_mappings"><GLMappings /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
